// Matches the default page names produced by `generateUniqueTitle` with a
// base of "Untitled" (e.g. "Untitled", "Untitled 2", "Untitled 3", ...).
// Used to decide when an AI-generated title may safely overwrite the page
// name. Keep this in sync with the `new`-mode naming logic below.
const DEFAULT_UNTITLED_NAME_PATTERN = /^Untitled(?:\s\d+)?$/;

export const isDefaultUntitledName = (
  name: string | undefined | null
): boolean =>
  typeof name === 'string' && DEFAULT_UNTITLED_NAME_PATTERN.test(name.trim());

export const generateUniqueTitle = (
  existingTitles: string[],
  baseTitle: string,
  mode: 'copy' | 'new' = 'new'
): string => {
  // Normalize helper – trim and replace multiple spaces with single
  const normalize = (str: string) => str.trim().replace(/\s+/g, ' ');

  const normalizedExisting = existingTitles.map(normalize);
  baseTitle = normalize(baseTitle);

  // Strip any existing copy prefixes to avoid nested copy prefixes
  const cleanedBase = baseTitle
    .replace(/^(\d+(?:st|nd|rd|th)\s)?Copy of\s/i, '')
    .trim();

  if (mode === 'new') {
    // Simple "Untitled", "Untitled 2", ... naming
    if (!normalizedExisting.includes(baseTitle)) return baseTitle;
    let counter = 2;
    let candidate = `${baseTitle} ${counter}`;
    while (normalizedExisting.includes(candidate)) {
      counter += 1;
      candidate = `${baseTitle} ${counter}`;
    }
    return candidate;
  }

  // Helper to get ordinal suffix (2nd, 3rd, 4th...)
  const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };

  // Detect existing copy indices for this base title
  const copyIndexPattern = new RegExp(
    `^(?:((\\d+)(?:st|nd|rd|th)?)\\s)?Copy of\\s${cleanedBase.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}$`,
    'i'
  );

  let maxIndex = 0; // 0 => no existing copies, 1 => "Copy of", 2 => "2nd Copy of" etc.

  normalizedExisting.forEach((title) => {
    const match = copyIndexPattern.exec(title);
    if (match) {
      if (match[2]) {
        const num = parseInt(match[2], 10);
        if (num > maxIndex) maxIndex = num;
      } else {
        // Plain "Copy of" counts as index 1
        if (maxIndex < 1) maxIndex = 1;
      }
    }
  });

  const nextIndex = maxIndex + 1;

  if (nextIndex === 1) {
    return `Copy of ${cleanedBase}`;
  }

  return `${getOrdinal(nextIndex)} Copy of ${cleanedBase}`;
};
